import React from 'react';
import { makeVar, useReactiveVar } from '@apollo/client';
import { isEqual, isUndefined, throttle, isFunction } from 'lodash';

import { Position } from 'types';
import { SurfaceNodePointsTo } from 'graphql/types';
import { usePrevious } from 'hooks';
import { notEqual } from 'utils';

interface SelectedLinkOperations {
  setSelectedLink: (selectedLink?: SurfaceNodePointsTo) => void;
  clearSelectedLink: () => void;
  toggleLinkSelection: (link: SurfaceNodePointsTo) => void;
  isLinkSelected: ({
    link,
    selectedLink,
  }: {
    link: SurfaceNodePointsTo;
    selectedLink?: SurfaceNodePointsTo;
  }) => boolean;
  getIsLinkSelected(link: SurfaceNodePointsTo): boolean;
}

type PositionFunction = (previousPosition: Position) => Position;

type SetLinkMenuPosition = (position?: Position | PositionFunction) => void;

const selectedLinkVar = makeVar<SurfaceNodePointsTo | undefined>(undefined);
const linkMenuPositionVar = makeVar<Position | undefined>(undefined);

const setLinkMenuPosition = throttle(
  (position?: Position | PositionFunction): void => {
    if (isFunction(position)) {
      linkMenuPositionVar(position(linkMenuPositionVar() as Position));
    } else {
      linkMenuPositionVar(position);
    }
  },
  100,
);

function clearLinkMenuPosition(): void {
  if (linkMenuPositionVar()) {
    linkMenuPositionVar(undefined);
  }
}

function setSelectedLink(selectedLink?: SurfaceNodePointsTo): void {
  selectedLinkVar(selectedLink);
}

function clearSelectedLink(): void {
  if (selectedLinkVar()) {
    selectedLinkVar(undefined);
  }
}

function toggleLinkSelection(link: SurfaceNodePointsTo) {
  const selectedLink = selectedLinkVar();

  if (selectedLink) {
    return selectedLinkVar(undefined);
  }

  if (isUndefined(selectedLink)) {
    return setSelectedLink(link);
  }

  setSelectedLink(link);
}

function isLinkSelected({
  link,
  selectedLink,
}: {
  link: SurfaceNodePointsTo;
  selectedLink?: SurfaceNodePointsTo;
}) {
  const reverseLink = { ...link, from: link.to, to: link.from };
  return isEqual(link, selectedLink) || isEqual(reverseLink, selectedLink);
}

function getIsLinkSelected(link: SurfaceNodePointsTo): boolean {
  const selectedLink = selectedLinkVar();

  const reverseLink = { ...link, from: link.to, to: link.from };
  return isEqual(link, selectedLink) || isEqual(reverseLink, selectedLink);
}

const selectedLinkOperations = {
  setSelectedLink,
  clearSelectedLink,
  toggleLinkSelection,
  isLinkSelected,
  getIsLinkSelected,
};

export function useSelectedLink(): [
  SurfaceNodePointsTo | undefined,
  SelectedLinkOperations,
] {
  const state = useReactiveVar(selectedLinkVar);

  return [state, selectedLinkOperations];
}

export function useLinkUnselectedListener(
  link: SurfaceNodePointsTo,
  callback: () => void,
) {
  const selectedLink = useReactiveVar(selectedLinkVar);

  const previousSelectedLink = usePrevious(selectedLink);

  React.useEffect(() => {
    if (isEqual(previousSelectedLink, link) && notEqual(link, selectedLink)) {
      callback();
    }
  }, [selectedLink, previousSelectedLink, link]);
}

export function useSelectedLinkOperations(): SelectedLinkOperations {
  return selectedLinkOperations;
}

export function useLinkMenuPosition(): [
  Position | undefined,
  SetLinkMenuPosition,
] {
  const linkPosition = useReactiveVar(linkMenuPositionVar);

  return [linkPosition, setLinkMenuPosition];
}

export function useLinkMenuPositionOperation(): {
  setLinkMenuPosition: SetLinkMenuPosition;
  clearLinkMenuPosition: () => void;
} {
  return { setLinkMenuPosition, clearLinkMenuPosition };
}
